import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Handshake, KeyRound, MapPin, ArrowRight, Phone, FileText, Eye, Download } from "lucide-react";

const AGENCY_IMAGE = "/hero-loroux.jpg";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const AgencePage = () => {
  const { settings } = useSettings();

  return (
    <div className="min-h-screen bg-white" data-testid="agence-page">
      <Header />

      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Notre agence</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-6" data-testid="agence-title">
            L’immobilier, avec proximité et transparence
          </h1>
          <p className="text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Implantée au Loroux-Bottereau, au cœur du Vignoble nantais, MAINGUY Immobilier accompagne propriétaires,
            acquéreurs, bailleurs et locataires dans leurs projets immobiliers sur le Vignoble nantais et la région nantaise.
          </p>

        </div>
      </section>

      {/* Conviction */}
      <section className="section-padding" data-testid="agence-conviction">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <img src={AGENCY_IMAGE} alt="Le Vignoble nantais, Le Loroux-Bottereau" className="w-full aspect-[4/5] object-cover" />
                <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] text-[#0A0A0A] p-6 hidden md:block">
                  <p className="font-cinzel text-xs uppercase tracking-[0.2em]">Le Loroux-Bottereau</p>
                  <p className="font-serif text-lg">Vignoble nantais</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Notre conviction</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-6">
                Un projet immobilier ne devrait jamais se résumer à une transaction
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed">
                <p>Derrière chaque vente, chaque achat ou chaque mise en location, il y a un projet de vie, un patrimoine ou un investissement.</p>
                <p className="text-[#0A0A0A] font-medium">Notre rôle est donc de vous conseiller avec sincérité, de vous accompagner avec attention et de vous dire les choses telles qu’elles sont.</p>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 border border-gray-200 bg-[#F9F9F9] px-7 py-6" data-testid="agence-fees">
                <div className="flex items-center gap-3 flex-1">
                  <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37]"><FileText size={20} /></span>
                  <div>
                    <p className="font-cinzel text-xs uppercase tracking-[0.2em] text-[#B8952B]">Nos Honoraires</p>
                    <p className="text-sm text-gray-500">Barème consultable et téléchargeable</p>
                  </div>
                </div>
                {settings.fees_pdf ? (
                  <div className="flex items-center gap-3">
                    <a href={`${API_BASE}${settings.fees_pdf}`} target="_blank" rel="noreferrer" data-testid="agence-fees-view">
                      <Button className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]">
                        <Eye size={16} className="mr-2" /> Consulter
                      </Button>
                    </a>
                    <a href={`${API_BASE}${settings.fees_pdf}?download=true`} data-testid="agence-fees-download">
                      <Button variant="outline" className="border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#D4AF37]">
                        <Download size={16} className="mr-2" /> Télécharger (PDF)
                      </Button>
                    </a>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 italic" data-testid="agence-fees-pending">Barème d'honoraires bientôt disponible</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Métier */}
      <section className="section-padding bg-[#F9F9F9]" data-testid="agence-metier">
        <div className="container-custom">
          <div className="max-w-3xl mb-14">
            <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Notre métier</p>
            <h2 className="font-serif text-3xl md:text-4xl">Vous conseiller, pas vous convaincre</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-white p-10 border border-gray-100 hover:border-[#D4AF37] transition-colors">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37] mb-6">
                <Handshake size={22} />
              </div>
              <h3 className="font-serif text-2xl mb-4">En transaction</h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="text-[#0A0A0A] font-medium">Nous privilégions un conseil juste et réaliste.</p>
                <p>Si votre bien est surestimé, nous vous le dirons. S’il mérite d’être valorisé différemment, nous vous l’expliquerons. Et si le moment n’est pas le bon pour vendre, nous saurons également vous le dire.</p>
                <p>Notre objectif n’est pas de décrocher un mandat à tout prix, mais de vous aider à prendre une décision éclairée et à construire un projet cohérent.</p>
              </div>
            </div>
            <div className="bg-white p-10 border border-gray-100 hover:border-[#D4AF37] transition-colors">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37] mb-6">
                <KeyRound size={22} />
              </div>
              <h3 className="font-serif text-2xl mb-4">En gestion locative</h3>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="text-[#0A0A0A] font-medium">Nous considérons votre bien comme un véritable patrimoine.</p>
                <p>Notre rôle ne s’arrête pas à trouver un locataire. Nous vous accompagnons dans la mise en location, la sélection des candidats, le suivi administratif et financier, mais aussi dans la gestion du quotidien : travaux, sinistres, relations avec les locataires et suivi de votre patrimoine.</p>
                <p>Nous privilégions une gestion rigoureuse et attentive, avec un objectif simple : vous permettre d’investir et de louer en toute sérénité, tout en restant maître de vos décisions.</p>
              </div>
              <Link to="/gestion" className="inline-flex items-center gap-2 mt-6 text-[#D4AF37] hover:text-[#0A0A0A] transition-colors text-sm uppercase tracking-wider" data-testid="agence-link-gestion">
                Découvrir la gestion locative <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Agence locale */}
      <section className="section-padding" data-testid="agence-locale">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A0A0A] text-[#D4AF37] mb-6">
                <MapPin size={22} />
              </div>
              <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Proximité</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                Une agence locale, indépendante et proche de ses clients
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-gray-600 leading-relaxed">
              <p>Nous avons fait le choix d’une agence à taille humaine, implantée au Loroux-Bottereau et profondément attachée à son territoire.</p>
              <p>Cette proximité nous permet de privilégier ce qui compte réellement pour nous : un interlocuteur direct, de la disponibilité et un suivi personnalisé. <span className="text-[#0A0A0A] font-medium">Vous n’êtes pas un dossier parmi d’autres et votre projet ne passe pas de main en main.</span></p>
              <p>Notre connaissance du Vignoble nantais et de la région nantaise nous permet de vous accompagner au plus près des réalités du marché local, avec des conseils adaptés à votre situation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="section-padding bg-[#0A0A0A] text-white" data-testid="agence-vision">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Notre vision de l’immobilier</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-10">
              Être l’agence que l’on recommande à ses proches
            </h2>
            <div className="space-y-5 text-gray-400 leading-relaxed text-lg">
              <p>Nous ne cherchons pas à être l’agence qui signe le plus de mandats ou réalise le plus de transactions.</p>
              <p className="text-white">Nous voulons être l’agence que l’on recommande à ses proches parce qu’on y a trouvé de l’écoute, de la transparence et une véritable implication.</p>
              <p>Chez MAINGUY Immobilier, nous croyons qu’une relation de confiance se construit dans le temps : par des conseils sincères, des engagements tenus et une présence réelle à chaque étape de votre projet.</p>
            </div>
          </div>
          <div className="mt-16 pt-10 border-t border-gray-800 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="agence-signature">
            {["Une agence locale.", "Un accompagnement sur mesure.", "Une parole qui compte."].map((t) => (
              <p key={t} className="font-serif text-2xl md:text-3xl text-[#D4AF37]">{t}</p>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="agence-cta">
        <div className="container-custom text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-6">Parlons de votre projet</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-10" data-testid="agence-cta-contact">
                Nous contacter
              </Button>
            </Link>
            <a href={`tel:${settings.phone?.replace(/\s/g, "")}`} className="flex items-center gap-2 text-gray-700 hover:text-[#D4AF37] transition-colors" data-testid="agence-cta-phone">
              <Phone size={18} /> {settings.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgencePage;
